// Analytics: Google Analytics (if configured) + first-party tracking that
// feeds the in-app dashboard at /admin → Analytics. Both run independently,
// so the dashboard works even when no GA measurement ID is set.
import { getAnonymousId } from './anon.js';
import { getAccessToken } from './api.js';

let loaded = false;

export function initGA() {
  if (typeof window === 'undefined' || loaded) return;
  const id = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!id) return;
  loaded = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  // SPA: we fire page_view ourselves on each route change (see trackPageView),
  // so disable the automatic initial pageview to avoid double counting.
  gtag('config', id, { send_page_view: false });
}

// --- First-party tracking --------------------------------------------------

const SESSION_KEY = 'mm-analytics-session-v1';
const SESSION_TTL = 30 * 60 * 1000; // 30 min inactivity → new session (GA-style)

function randomId() {
  try {
    return crypto.randomUUID?.() ?? (Math.random().toString(36).slice(2) + Date.now().toString(36));
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

function getSessionId() {
  if (typeof window === 'undefined') return null;
  try {
    const now = Date.now();
    let s = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
    if (!s || !s.id || now - s.last > SESSION_TTL) {
      s = { id: 'sess_' + randomId(), last: now };
    } else {
      s.last = now;
    }
    localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    return s.id;
  } catch {
    return null;
  }
}

function timezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
}

function utmParams() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utmSource: p.get('utm_source') || undefined,
      utmMedium: p.get('utm_medium') || undefined,
      utmCampaign: p.get('utm_campaign') || undefined,
    };
  } catch {
    return {};
  }
}

// Keep the admin's own dashboard browsing out of the traffic numbers.
function shouldTrack(path) {
  return typeof path === 'string' && !path.startsWith('/admin');
}

function basePayload(path) {
  return {
    path: String(path || '').slice(0, 512),
    referrer: (typeof document !== 'undefined' ? document.referrer : '') || '',
    sessionId: getSessionId(),
    visitorId: getAnonymousId(),
    language: typeof navigator !== 'undefined' ? navigator.language : null,
    timezone: timezone(),
    screenW: typeof window !== 'undefined' && window.screen ? window.screen.width : null,
    screenH: typeof window !== 'undefined' && window.screen ? window.screen.height : null,
    ...utmParams(),
  };
}

function collect(body) {
  if (typeof window === 'undefined') return;
  try {
    const headers = { 'Content-Type': 'application/json' };
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch('/api/analytics/collect', {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => {});
  } catch {
    /* never let analytics throw */
  }
}

export function trackPageView(path) {
  if (typeof window === 'undefined') return;
  const id = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (window.gtag && id) {
    window.gtag('event', 'page_view', { page_path: path, send_to: id });
  }
  if (shouldTrack(path)) collect({ type: 'pageview', ...basePayload(path) });
}

export function trackEvent(name, params = {}) {
  if (typeof window === 'undefined' || !name) return;
  if (window.gtag) window.gtag('event', name, params);
  const path = (window.location?.pathname || '') + (window.location?.search || '');
  if (shouldTrack(window.location?.pathname || '')) {
    collect({ type: 'event', name: String(name).slice(0, 64), ...basePayload(path), metadata: params });
  }
}
